import { isValidPhoneNumber } from 'libphonenumber-js/mobile';
import * as Yup from 'yup';

/**
 * Yup schema for country object
 * Used in dual field validation pattern
 *
 * @example
 * country: countryCodeSchema
 */
export const countryCodeSchema = Yup.object({
  name: Yup.string().required('Country name is required'),
  calling_code: Yup.string().required('Calling code is required'),
  code: Yup.string().required('Country code is required')
});

export type CountryCodeFormValue = Yup.InferType<typeof countryCodeSchema>;

export type PhoneNumberValidationValues<
  PhoneFieldName extends string = 'mobile',
  CountryFieldName extends string = 'country'
> = Record<PhoneFieldName, string> & Record<CountryFieldName, CountryCodeFormValue>;

export type PhoneNumberValidationSchema<
  PhoneFieldName extends string = 'mobile',
  CountryFieldName extends string = 'country'
> = Yup.ObjectSchema<PhoneNumberValidationValues<PhoneFieldName, CountryFieldName>>;

/**
 * Helper function to create phone number validation for nested field paths
 * Useful when phone fields are nested in objects (e.g., basic.mobile, basic.country)
 *
 * @param phoneFieldName - Name of the phone field (e.g., "mobile")
 * @param countryFieldName - Name of the country field (e.g., "country")
 * @returns Yup schema object with validation for both fields
 *
 * @example
 * // For nested fields like basic.mobile and basic.country
 * const schema = Yup.object({
 *   basic: Yup.object({
 *     ...createPhoneNumberValidation('mobile', 'country').fields,
 *     // other basic fields...
 *   })
 * });
 */
export const createPhoneNumberValidation = <
  PhoneFieldName extends string = 'mobile',
  CountryFieldName extends string = 'country'
>(
  phoneFieldName: PhoneFieldName = 'mobile' as PhoneFieldName,
  countryFieldName: CountryFieldName = 'country' as CountryFieldName,
  isOptional = false
): PhoneNumberValidationSchema<PhoneFieldName, CountryFieldName> => {
  const mobileFieldSchema = isOptional
    ? Yup.string().nullable()
    : Yup.string().required('Phone number is required');

  const countryFieldSchema = isOptional
    ? countryCodeSchema.nullable().notRequired()
    : countryCodeSchema.required('Country is required');
  return Yup.object({
    [phoneFieldName]: mobileFieldSchema.test(
      'valid-phone',
      'Invalid phone number',
      function (val) {
        if (!val) return isOptional;
        const country = this.parent[countryFieldName];
        if (!country?.calling_code) return false;
        return isValidPhoneNumber(`+${country.calling_code}-${val}`);
      }
    ),
    [countryFieldName]: countryFieldSchema
  }) as PhoneNumberValidationSchema<PhoneFieldName, CountryFieldName>;
};
